import * as React from 'react';
import memoizeOne from 'memoize-one';
import isPrintableKeyEvent from 'is-printable-key-event';
import validate from './validations';
import Component from './component';
import unpick from '../utilities/unpick';
import { unsafeSend } from '../../iframes/communication';
import { cardTypeFromNumber, extendCardList, extendCardListWithUnknown, payform } from '../utilities/payform';
import { filterFramesByFormId, filterFramesByFieldId, filterFramesByFieldType } from '../../iframes/filters';
import { isObject } from '../../../../src/utilities/is-object';
import { CollectError } from '../../config';
import { FIELD_TYPE } from '../constants';

type ControllerProps = {
  type: string;
  valueWhenChecked?: string;
  globalValue: any;
  fieldState: any;
  addCardBrands?: any;
  validations: Array<string | { type: string; params: any }>;
  onUpdate: (state: any, eventType: string) => void;
  onKeyboardEvent: (event: VGSKeyboardEventData) => void;
  onFocusEvent: (event: VGSFocusEventData) => void;
  defaultValue?: string | undefined;
  cardType?: string;
  name: string;
  formId: string;
  cvcDependency?: string | undefined;
  showCardIcon?: boolean | Record<string, any>;
};

const isEnterPress = (event: React.SyntheticEvent<Element, KeyboardEvent>) => {
  const code = event.nativeEvent.keyCode || event.nativeEvent.which;
  return event.type === 'keypress' && code === 13;
};

function getValidationMeta(validation: ControllerProps['validations'][0]) {
  if (isObject(validation)) {
    return {
      type: validation.type,
      params: validation.params,
    };
  } else if (typeof validation === 'string') {
    return {
      type: validation,
      params: null,
    };
  }
  throw new CollectError('Validation element must be a string or an object');
}

function _coerceValidations(validations) {
  if (typeof validations === 'string') {
    return { [validations]: null };
  } else if (Array.isArray(validations)) {
    return validations.reduce((acc, cur) => {
      const { type, params } = getValidationMeta(cur);
      acc[type] = params;
      return acc;
    }, {});
  }
  return {};
}
const coerceValidations = memoizeOne(_coerceValidations);

const INNER_VALIDATIONS = {
  [FIELD_TYPE.FILE]: ['validFileSize', 'validFileName', 'validFilesLength'],
};

/**
 * Map validation to the array of rules that needs to be excluded from the check.
 */
const REDUNDANT_VALIDATION_RULES = {
  [FIELD_TYPE.CARD_NUMBER]: {
    validCardNumberExtended: ['validCardNumber'],
    validCardNumberLuhnCheck: ['validCardNumber', 'validCardNumberExtended'],
  },
};

/**
 * We have multiple alternative validation rules available for some fields.
 * We have to exclude unnecessary rules to avoid validation misconfiguration and excessive checks.
 */
const excludeRedundantRules = (validations, redundantRules) => {
  validations.forEach((rule) => {
    if (rule in redundantRules) {
      validations = validations.filter((validation) => !redundantRules[rule].includes(validation));
    }
  });
  return validations;
};

function getAllValidations(validations, type: string) {
  /**
   * Validation as string passed in tests.
   */
  if (typeof validations === 'string') {
    validations = [validations];
  }

  if (INNER_VALIDATIONS[type] && validations) {
    validations = [...validations, ...INNER_VALIDATIONS[type]];
  }

  if (REDUNDANT_VALIDATION_RULES[type] && validations) {
    validations = excludeRedundantRules(validations, REDUNDANT_VALIDATION_RULES[type]);
  }
  return coerceValidations(validations);
}

type LimitedKeyboardEvent = KeyboardEvent & { type: 'keypress' | 'keydown' | 'keyup' };

function isKeyboardEvent<T>(event: React.SyntheticEvent<T>): event is React.SyntheticEvent<T, LimitedKeyboardEvent> {
  return event.nativeEvent instanceof KeyboardEvent;
}

type LimitedFocusEvent = FocusEvent & { type: 'focus' | 'blur' };

function isFocusEvent<T>(event: React.SyntheticEvent<T>): event is React.SyntheticEvent<T, LimitedFocusEvent> {
  return event.nativeEvent instanceof FocusEvent && (event.type === 'focus' || event.type === 'blur');
}

class KeyboardCharIndex {
  #previousIndex = -1;
  #chars = new Map<string, number>();

  /**
   * Returns an existing index for a specified key.
   * If no index exists for a key, a new one is returned
   * @param {string} key
   */
  get(key: string): number {
    const index = this.#chars.get(key);

    if (typeof index === 'number') {
      return index;
    } else {
      const next = this.#previousIndex + 1;
      this.#previousIndex = next;
      this.#chars.set(key, next);
      return next;
    }
  }

  removeChar(char: string): void {
    this.#chars.delete(char);
  }
}

export default class Controller extends React.Component<ControllerProps> {
  private inputElement: React.RefObject<HTMLElement>;

  constructor(props) {
    super(props);
    this.inputElement = React.createRef();
  }

  componentDidUpdate(prevProps) {
    const { addCardBrands, type, cardType, globalValue } = this.props;
    if (addCardBrands && prevProps.addCardBrands !== addCardBrands) {
      extendCardList(addCardBrands);
    }
    if (type === 'card-security-code' && prevProps.cardType !== cardType) {
      this.resetState(this.props.fieldState.value, 'validate');
    } else if (globalValue !== prevProps.globalValue) {
      this.resetState(this.props.fieldState.value, 'validate');
    } else if (prevProps.validations !== this.props.validations) {
      this.resetState(this.props.fieldState.value, 'validate');
    }
  }

  componentDidMount() {
    const { defaultValue, validations } = this.props;
    this.hackForIOSFocus();
    extendCardListWithUnknown(validations);
    if (typeof defaultValue !== 'undefined') {
      this.resetState(defaultValue, 'init');
    } else {
      this.resetState('', 'init');
    }
  }

  #keyboardIndex = new KeyboardCharIndex();

  /**
   * Sends keyboard event data to the
   */
  handleKeyboardEvent = (event: React.SyntheticEvent<any, LimitedKeyboardEvent>) => {
    const { nativeEvent } = event;
    const isPrintable = isPrintableKeyEvent(nativeEvent);
    const ctrlPressed = nativeEvent.metaKey || nativeEvent.ctrlKey;
    const canExposeKey = !isPrintable || ctrlPressed;

    const keyIndex = this.#keyboardIndex.get(nativeEvent.key);

    const params = {
      type: nativeEvent.type,
      timeStamp: nativeEvent.timeStamp,
      isTrusted: nativeEvent.isTrusted,
      key: canExposeKey ? nativeEvent.key : null,
      keyCode: canExposeKey ? nativeEvent.keyCode : null,
      which: canExposeKey ? nativeEvent.which : null,
      metaKey: nativeEvent.metaKey,
      ctrlKey: nativeEvent.ctrlKey,
      valueHidden: !canExposeKey,
      keyIndex,
    };

    // at this moment the key is not pressed anymore
    if (nativeEvent.type === 'keyup') {
      this.#keyboardIndex.removeChar(nativeEvent.key);
    }
    this.props.onKeyboardEvent(params);
  };

  handleFocusEvent = (event: React.SyntheticEvent<any, LimitedFocusEvent>) => {
    const { nativeEvent } = event;
    const params = {
      type: nativeEvent.type,
      timeStamp: nativeEvent.timeStamp,
      isTrusted: nativeEvent.isTrusted,
    };
    this.props.onFocusEvent(params);
  };

  onUpdate = (event: React.SyntheticEvent, customValue?: string) => {
    if (isKeyboardEvent(event)) {
      this.handleKeyboardEvent(event);
    } else if (isFocusEvent(event)) {
      this.handleFocusEvent(event);
    }

    const target = event.target as HTMLInputElement;
    let correctedValue;

    if (['checkbox', 'radio'].includes(this.props.type)) {
      this.resetState(target.checked, event.type);
    } else if (this.props.type !== 'textarea' && isKeyboardEvent(event) && isEnterPress(event)) {
      unsafeSend(window.parent, 'enterPress', {
        name: this.props.name,
        formId: this.props.formId,
      });
    } else if (this.props.type === 'file') {
      correctedValue = target.files;
      this.resetState(correctedValue, event.type);
    } else {
      correctedValue = target.value;
      if (typeof customValue !== 'undefined') {
        // customValue is passed for password-like masks
        correctedValue = customValue;
      }
      this.resetState(correctedValue, event.type);
    }
  };

  getErrorMessages(value: string | boolean | undefined) {
    const { validations: fieldValidation, type } = this.props;
    const validations = getAllValidations(fieldValidation, type);
    return validate({
      value,
      validations,
      globalValue: this.props.globalValue,
      fieldProps: this.props,
    });
  }

  hackForIOSFocus = () => {
    const { userAgent } = window.navigator;
    const iOS = /iPad/i.test(userAgent) || /iPhone/i.test(userAgent);
    const webkit = /WebKit/i.test(userAgent);
    const iOSSafari = iOS && webkit && !/CriOS/i.test(userAgent);
    if (iOS && webkit && iOSSafari) {
      document.body.addEventListener('click', this.hackForIOSTouch);
    }
  };

  hackForIOSTouch = () => {
    if (this.props.type !== 'file') {
      const activeElement = () => document.activeElement as HTMLInputElement;
      activeElement().value += ' ';
      activeElement().value = activeElement().value.slice(0, -1);
    }
  };

  resetState(value: string | boolean = '', eventType = '') {
    const element = this.inputElement.current;

    let adjustedValue: string | boolean | undefined = value;
    if (['checkbox', 'radio'].includes(this.props.type)) {
      adjustedValue = value ? this.props.valueWhenChecked : '';
    }

    const errorMessages = this.getErrorMessages(adjustedValue);
    const state = {
      ...this.props.fieldState,
      errorMessages,
      value: adjustedValue,
      isValid: errorMessages.length === 0,
      isFocused:
        document.activeElement === element &&
        eventType !== 'blur' &&
        !!((element as HTMLInputElement).type || (element as HTMLAnchorElement).href),
      isTouched: !this.props.fieldState.isTouched && eventType === 'blur' ? true : this.props.fieldState.isTouched,
    };

    if (adjustedValue !== this.props.fieldState.value) {
      state.isDirty = true;
    }
    if (typeof adjustedValue === 'string' && adjustedValue.length === 0) {
      state.isEmpty = true;
    } else {
      state.isEmpty = false;
    }
    if (this.props.type === 'card-number') {
      const cardType = cardTypeFromNumber(adjustedValue);

      if (state.cardType !== cardType) {
        const cardTypeListener = this.props.cvcDependency
          ? filterFramesByFieldId(this.props.cvcDependency, filterFramesByFieldType('card-security-code'))
          : filterFramesByFormId(this.props.formId, filterFramesByFieldType('card-security-code'));

        cardTypeListener.forEach((field) => {
          unsafeSend(field, 'changeCardType', { cardType, cards: payform.cards });
        });
      }
      state.cardType = cardType;
      state.last4 = null;
      state.bin = null;
      if (state.isValid && state.value.length >= 12) {
        state.last4 = state.value.replace(/\s+/g, '').slice(-4);
      }
      if (state.isValid && state.value.length >= 12) {
        state.bin = state.value.replace(/\s+/g, '').slice(0, 6);
      }
    }
    if (this.props.type === 'ssn') {
      state.last4 = null;
      if (state.isValid && state.value.length > 4) {
        state.last4 = state.value.replace(/\s+/g, '').slice(-4);
      }
    }
    if (this.props.type === 'file') {
      state.files = [];
      const { files } = element as HTMLInputElement;
      if (files && files.length > 0) {
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          state.files.push({
            name: file.name,
            size: file.size,
            type: file.type,
            lastModified: file.lastModified,
          });
        }
      }
    }

    this.props.onUpdate(state, eventType);
  }

  render() {
    let props = unpick(this.props, [
      'name',
      'validations',
      'onUpdate',
      'defaultValue',
      'addCardBrands',
      'cvcDependency',
      'formId',
      'globalValue',
      'onKeyboardEvent',
      'onFocusEvent',
    ]);
    if (this.props.type !== 'card-expiration-date') {
      props = unpick(props, ['yearLength']);
    }
    if (this.props.showCardIcon && this.props.type === 'card-number') {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      props.cardType = this.props.fieldState.cardType || '';
    }
    return <Component {...props} onUpdate={this.onUpdate} ref={this.inputElement} />;
  }
}
